import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { Hub } from 'aws-amplify/utils';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider, useApp } from './contexts/AppContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import useAppStore from './stores/useAppStore';
import useNativeSetup from './hooks/useNativeSetup';
import AuthModal from './components/AuthModal';
import Sidebar from './components/Sidebar';
import TaskDetail from './components/TaskDetail';
import CommandPalette from './components/CommandPalette';
import TrialBanner from './components/TrialBanner';
import NudgeBanner from './components/NudgeBanner';
import AppHeader from './components/AppHeader';
import BottomNav from './components/BottomNav';
import './App.css';

// Code-split views — keeps the initial bundle lean.
const InboxView = lazy(() => import('./views/InboxView'));
const TodayView = lazy(() => import('./views/TodayView'));
const UpcomingView = lazy(() => import('./views/UpcomingView'));
const AnytimeView = lazy(() => import('./views/AnytimeView'));
const SomedayView = lazy(() => import('./views/SomedayView'));
const LogbookView = lazy(() => import('./views/LogbookView'));
const ProjectView = lazy(() => import('./views/ProjectView'));
const ProfileView = lazy(() => import('./views/ProfileView'));

// Lazy overlays — loaded on first open
const FocusMode = lazy(() => import('./components/FocusMode'));
const BrainDump = lazy(() => import('./components/BrainDump'));

function ViewSpinner() {
  return (
    <div className="app-loading">
      <div className="loading-spinner" />
    </div>
  );
}

// Handles the OAuth redirect from Cognito (Apple Sign In web flow)
function OAuthCallbackHandler() {
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    const listener = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        setStatus('success');
        window.history.replaceState({}, '', '/');
        window.location.reload();
      }
      if (payload.event === 'signInWithRedirect_failure') {
        console.error('[Auth] OAuth callback failure:', payload);
        toast.error('Sign in failed. Please try again.');
        setStatus('error');
        setTimeout(() => {
          window.history.replaceState({}, '', '/');
          window.location.reload();
        }, 2000);
      }
    });

    const timeout = setTimeout(() => {
      if (status === 'processing') {
        setStatus('timeout');
        window.history.replaceState({}, '', '/');
        window.location.reload();
      }
    }, 10000);

    return () => {
      listener();
      clearTimeout(timeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="app-loading">
      <div className="loading-spinner" />
      <p>{status === 'error' ? 'Sign in failed. Redirecting...' : 'Completing sign in...'}</p>
    </div>
  );
}

function AppContent() {
  const { state, dispatch } = useApp();
  const { isAuthenticated } = useAuth();
  const { refreshProfile } = useSubscription();

  // Once Brain Dump has been opened it stays mounted (it renders null when
  // closed) so the typed text survives an accidental Escape/close.
  const [brainDumpMounted, setBrainDumpMounted] = useState(false);
  useEffect(() => {
    if (state.brainDumpOpen) setBrainDumpMounted(true);
  }, [state.brainDumpOpen]);

  // Handle checkout success redirect — retry until webhook activates subscription
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') !== 'success') return;
    window.history.replaceState({}, '', window.location.pathname);
    toast.success('Payment received! Activating your subscription...');

    let retries = 0;
    const maxRetries = 5;
    const poll = async () => {
      await refreshProfile();
      retries++;
      if (retries < maxRetries) {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [refreshProfile]);

  // Deep links: ?view=today|inbox|... switches view; ?braindump=1 / ?focus=1 open overlays.
  // Used by nudge-email CTAs and tooling; harmless in normal use.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    if (view && ['inbox', 'today', 'upcoming', 'anytime', 'someday', 'logbook', 'profile'].includes(view)) {
      dispatch({ type: 'SET_ACTIVE_VIEW', payload: view });
    }
    if (params.get('braindump') === '1') dispatch({ type: 'OPEN_BRAIN_DUMP' });
    if (params.get('focus') === '1') dispatch({ type: 'OPEN_FOCUS_MODE' });
  }, [dispatch]);

  const renderView = () => {
    if (state.activeProjectId) {
      return <ProjectView />;
    }

    switch (state.activeView) {
      case 'inbox':
        return <InboxView />;
      case 'today':
        return <TodayView />;
      case 'upcoming':
        return <UpcomingView />;
      case 'anytime':
        return <AnytimeView />;
      case 'someday':
        return <SomedayView />;
      case 'logbook':
        return <LogbookView />;
      case 'profile':
        return <ProfileView />;
      default:
        return <InboxView />;
    }
  };

  if (state.isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
        <p>Loading your tasks...</p>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="app-loading">
        <p style={{ color: 'var(--accent-red)', marginBottom: 12 }}>{state.error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--accent-blue)', color: 'white', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="app min-h-screen bg-gray-50 dark:bg-night-bg pb-16 md:pb-0 md:pl-64">
      {/* Mobile sidebar overlay (shown when collapsed === false on mobile) */}
      <div
        className={`sidebar-overlay${!state.sidebarCollapsed ? ' visible' : ''}`}
        onClick={() => dispatch({ type: 'TOGGLE_SIDEBAR' })}
      />
      <Sidebar />

      <AppHeader />

      <main className="main-content">
        {isAuthenticated && <TrialBanner />}
        {isAuthenticated && <NudgeBanner />}
        <div className="view-container">
          <Suspense fallback={<ViewSpinner />}>{renderView()}</Suspense>
        </div>
        {state.selectedTaskId && (
          <div className="detail-panel">
            <TaskDetail />
          </div>
        )}
      </main>

      <BottomNav />
      <CommandPalette />
      {state.focusModeOpen && (
        <Suspense fallback={null}>
          <FocusMode />
        </Suspense>
      )}
      {brainDumpMounted && (
        <Suspense fallback={null}>
          <BrainDump />
        </Suspense>
      )}
      <AuthModal />
    </div>
  );
}

function ThemeSync() {
  const nightMode = useAppStore((s) => s.nightMode);
  const setNightMode = useAppStore((s) => s.setNightMode);
  const initializedRef = React.useRef(false);

  // Apply class on documentElement (canonical pattern requires <html>, not a wrapper)
  useEffect(() => {
    document.documentElement.classList.toggle('dark', nightMode);
  }, [nightMode]);

  // On first mount, sync from system preference if user hasn't explicitly chosen yet.
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const persisted = (() => {
      try {
        const raw = localStorage.getItem('docket-app');
        return raw ? JSON.parse(raw)?.state?.nightMode : undefined;
      } catch { return undefined; }
    })();
    if (persisted == null) {
      setNightMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
  }, [setNightMode]);

  // Live-follow OS theme changes.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setNightMode(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [setNightMode]);

  return null;
}

function App() {
  // StatusBar style, SplashScreen.hide, deeplink + back-button handlers (native only)
  useNativeSetup();

  // Handle OAuth callback route (Apple Sign In redirect)
  const isOAuthCallback = window.location.pathname === '/auth/callback';

  return (
    <AuthProvider>
      <ThemeSync />
      <Toaster
        position="top-center"
        containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
        toastOptions={{
          duration: 3500,
          style: {
            borderRadius: '14px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
            maxWidth: '92vw',
            background: 'var(--toast-bg)',
            color: 'var(--toast-fg)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
            border: '1px solid var(--toast-border)',
          },
          success: { iconTheme: { primary: 'var(--app-accent)', secondary: '#fff' }, duration: 2500 },
          error: { iconTheme: { primary: '#ef4444', secondary: '#fff' }, duration: 4500 },
        }}
      />
      {isOAuthCallback ? (
        <OAuthCallbackHandler />
      ) : (
        <SubscriptionProvider>
          <AppProvider>
            <AppContent />
          </AppProvider>
        </SubscriptionProvider>
      )}
    </AuthProvider>
  );
}

export default App;
